export type FeatureSection = { title: string; body: string; bullets?: string[]; kicker?: string };
export type FeatureBenefit = { title: string; text: string };
export type FeatureFaq = { q: string; a: string };

export type FeaturePageContent = {
  slug: string;
  eyebrow: string;
  title: string;
  lead: string;
  heroBullets: string[];
  sections: FeatureSection[];
  benefits: FeatureBenefit[];
  faq: FeatureFaq[];
  seo: { title: string; description: string };
};

export const FEATURE_PAGES: Record<string, FeaturePageContent> = {
  sales: {
    slug: "sales",
    eyebrow: "Sales",
    title: "Sales orders under control. Always",
    lead: "Forget Excel chaos and missed deadlines. Your customers receive orders on time, and you gain more repeat sales.",
    heroBullets: [
      "All customer orders in one place — clear and simple",
      "Always know delivery dates and order statuses",
      "Real-time stock booking — no mistakes, no false promises",
    ],
    sections: [
      {
        title: "Orders that work on their own",
        body: "Every stage from customer request to shipment — automated and under control.",
        bullets: [
          "Order creation — the manager creates an order based on a request or quotation",
          "Stock check — the system displays product availability and reserves batches automatically",
          "Status control — you always know whether an order is confirmed or in progress",
          "Shipment and documentation — the system generates delivery notes and records fulfillment",
        ],
      },
      {
        kicker: "Scope",
        title: "What's in the box",
        body: "Everything a sales team needs to promise dates it can keep.",
        bullets: [
          "Creation and management of sales orders",
          "Creation of quotations and converting them into orders",
          "Automatic tracking of order statuses (new, in progress, shipped, completed)",
          "Monitoring fulfillment and delivery deadlines",
          "Real-time product availability check and stock reservation",
          "Generating shipment and return documents",
          "Full transparency of customer order fulfillment",
        ],
      },
      {
        kicker: "Why it works",
        title: "Availability you can actually promise",
        body: "Sales sits on top of live production data, so a confirmed date is a date the shop floor can hold.",
        bullets: [
          "Order created straight from an inquiry or quotation, with no re-keying",
          "Real-time availability check with allocation of specific batches to the order",
          "Delivery-date control against the production plan, not against a wish",
          "Partial shipments and backorders tracked per order line, not in a notebook",
          "Goods issue and return notes that update stock and order status on posting",
          "Customer service level tracked per order line, the way manufacturers measure OTIF",
          "Price lists and discounts applied consistently across quotations and orders",
          "Every promise traceable: who confirmed which date, batch and quantity",
        ],
      },
    ],
    benefits: [
      { title: "Transparent sales overview", text: "See order volumes, fulfillment, and projected revenue in real time." },
      { title: "Fulfilling customer commitments", text: "Assurance that delivery deadlines are tracked and met." },
      { title: "Synchronization of production and sales", text: "Orders are instantly transformed into production tasks." },
      { title: "Improved cash flow", text: "Fast invoice and delivery note generation keeps receivables moving." },
    ],
    faq: [
      { q: "Can orders check stock automatically?", a: "Yes. The system checks real-time availability and reserves batches the moment an order is created." },
      { q: "Does it generate shipping documents?", a: "Yes, delivery notes and returns are generated automatically and update stock and order status." },
      { q: "Can I convert a quotation into an order?", a: "Yes, quotations convert directly into sales orders without re-entering data." },
      { q: "How does allocation differ from a simple stock check?", a: "A stock check tells you what is there today; allocation ties specific batches to an order line so nobody else can promise them." },
      { q: "Can sales see production progress without calling the shop floor?", a: "Yes. Manufacturing progress and fulfilment dates stay visible against the customer order, so sales can answer customers from live data." },
      { q: "Can one sales order have several delivery dates?", a: "Yes. Delivery schedules keep planned quantities and dates at line level, making phased customer commitments clear." },
    ],
    seo: {
      title: "Manufacturing Sales Order Software | factory.online",
      description: "Control quotes, sales orders, phased deliveries, stock reservations and manufacturing progress without spreadsheet handoffs.",
    },
  },

  production: {
    slug: "production",
    eyebrow: "Production",
    title: "Production without delays or excuses",
    lead: "The system automatically calculates material and capacity demand. You see real deadlines and deliver orders without chaos or rush.",
    heroBullets: [
      "Planning to order or to stock",
      "Automatic material and capacity requirement calculation",
      "Real-time control of order execution and deadlines",
    ],
    sections: [
      {
        title: "Production without chaos",
        body: "The system plans resources automatically — you just oversee timing and performance.",
        bullets: [
          "Order creation — a job order is created with all parameters defined",
          "Requirements planning — material, semi-finished goods and capacity needs are calculated automatically",
          "Order execution — statuses and due dates are tracked as work progresses",
          "Performance monitoring — finished goods and defects are recorded with actual operation times",
        ],
      },
      {
        title: "What's in the box",
        body: "Manufacturing orders, requirements planning and finished-goods receipt on one backbone.",
        bullets: [
          "Create manufacturing orders based on sales orders or warehouse needs",
          "Material and semi-finished goods requirements planning (MRPII, MRP)",
          "Manage manufacturing order statuses, due dates and change tracking",
          "Recording of finished goods output and defects with actual operation times",
          "Finished goods receipt to warehouse — to a specific order or free stock",
          "Automatic commitment of work-in-progress for manufacturing orders",
          "Synchronization with up-to-date BOM and routing versions",
        ],
      },
      {
        title: "Make-to-order and make-to-stock, side by side",
        body: "Manufacturing orders carry the parameters production actually needs — batch size, marking and packing requirements included.",
        bullets: [
          "MTO orders raised from sales demand, MTS orders from stock policy",
          "Batch sizes, marking and packing requirements defined on the order",
          "Material and capacity demand exploded from the current BOM and routing",
          "Job orders generated per operation, ready for scheduling",
          "Actual output, scrap and operation times recorded against the order",
        ],
      },
      {
        title: "Job order aggregation for drum-based production",
        body: "For cable and wire plants the drum, not the order, is the real production unit — so the system plans around it.",
        bullets: [
          "Best-fit drum selection under weight and capacity constraints",
          "Flange, barrel and traverse geometry taken into account",
          "Compatible order lines aggregated onto a single drum run",
          "Remaining capacity offered to the next order instead of being scrapped",
          "Fewer changeovers, less waste, more metres per shift",
        ],
      },
    ],
    benefits: [
      { title: "Fewer delays", text: "Real deadlines calculated from actual capacity, not guesswork." },
      { title: "Higher work centre productivity", text: "Bottlenecks and overloads are visible before they cost you a shift." },
      { title: "Less manual planning", text: "Material and resource requirements are calculated automatically." },
    ],
    faq: [
      { q: "Can I plan to stock and to order at the same time?", a: "Yes, MTS, MTO and ATO strategies are all supported." },
      { q: "How is work-in-progress tracked?", a: "WIP is committed automatically as manufacturing orders progress, giving a real-time view of value on the shop floor." },
      { q: "What happens when a BOM changes mid-order?", a: "You choose whether to finish with the old version or switch open orders to the updated BOM or routing." },
      { q: "Can several orders share one drum?", a: "Yes. Compatible lines are aggregated onto a best-fit drum under weight and capacity constraints, which cuts changeovers and waste." },
      { q: "Can approvals and production discussions stay with the document?", a: "Yes. Approval steps, attachments and working discussions remain connected to the relevant order or batch." },
      { q: "Can access be limited by factory or warehouse location?", a: "Yes. Location-aware permissions keep distributed teams focused on the operations they are responsible for." },
    ],
    seo: {
      title: "Production & Manufacturing Order Software | factory.online",
      description: "Control manufacturing orders, approvals, execution progress, finished-goods receipt and audit history from one production system.",
    },
  },

  purchases: {
    slug: "purchases",
    eyebrow: "Supply Chain",
    title: "Supply that works for you — not against you",
    lead: "No surprises, no \"stuck\" orders. You know exactly what's coming and when, keeping your entire supply chain under control.",
    heroBullets: [
      "Automated purchase requests and purchase orders",
      "Receipt-to-order linking and due date tracking",
      "Transparent fulfillment status — from request to delivery",
    ],
    sections: [
      {
        title: "Procurement without delays",
        body: "From request to material arrival — full real-time transparency.",
        bullets: [
          "Purchase request creation, based on shortage forecasts or manual demand",
          "Transformation into a supplier purchase order",
          "Status monitoring — pending, partially received, fully received",
          "Receipt and updates — statuses refresh automatically from goods receipt",
        ],
      },
      {
        title: "What's in the box",
        body: "Requisitions, purchase orders and a procurement strategy for every material.",
        bullets: [
          "Creation and management of purchase requisitions",
          "Converting requisitions into supplier purchase orders",
          "Control over delivery due dates and on-time performance",
          "Forecasting material requirements and managing safety stock and lead times",
          "Generating purchase requisitions based on shortage forecasts",
          "Procurement strategy per material: safety stock, actual demand, reorder point, or fixed schedule",
        ],
      },
      {
        title: "One thread from requisition to receipt",
        body: "Every purchase keeps its history: what was asked for, what was ordered, what actually arrived and when.",
        bullets: [
          "Requisitions raised from shortage forecasts, reorder points or manual demand",
          "Conversion into supplier purchase orders with agreed lead times",
          "Partial and full receipt statuses per line, so split deliveries stay visible",
          "GRN posting updates stock, batches and the purchase order in one step",
          "Delivery-date monitoring that exposes the suppliers who quietly cost you shifts",
        ],
      },
    ],
    benefits: [
      { title: "Fewer delayed deliveries", text: "Due dates and supplier performance are tracked automatically." },
      { title: "Lower procurement costs", text: "Buy against real demand instead of a buffer built on habit." },
      { title: "Less time on paperwork", text: "Requests and orders are generated and updated automatically." },
    ],
    faq: [
      { q: "Can purchase requests be generated automatically?", a: "Yes, based on shortage forecasts, safety stock levels or reorder points." },
      { q: "How does the system track supplier performance?", a: "Delivery due dates and on-time performance are tracked per order and per supplier." },
      { q: "Can I set a different procurement strategy per material?", a: "Yes — safety stock, actual demand, reorder point, or fixed schedule, chosen per item." },
      { q: "What happens on a partial delivery?", a: "The line moves to partially received, remaining quantity stays open, and the purchase order keeps its own due-date history." },
      { q: "Can supplier delivery schedules feed material planning?", a: "Yes. Expected dates and quantities are recorded by order line and stay visible to material planning and shortage control." },
      { q: "Can purchased services be received like materials?", a: "Yes. Services and subcontracted work can be ordered and received alongside materials for a complete purchasing-cost picture." },
    ],
    seo: {
      title: "Supply Chain & Purchasing Software | factory.online",
      description: "Turn purchase requisitions into supplier orders, track phased deliveries and receive materials or services against real demand.",
    },
  },

  stocks: {
    slug: "stocks",
    eyebrow: "Inventory Management",
    title: "Inventory without chaos or blind spots",
    lead: "Stock levels always up-to-date, every batch traceable. No more \"Where is it stored?\" or \"Why is it already expired?\"",
    heroBullets: [
      "Always know stock levels across all storage locations in real time",
      "Full batch traceability with QR codes and expiration dates",
      "Automatic updates on movements and stocktakes",
    ],
    sections: [
      {
        title: "Inventory always under control",
        body: "No blind spots — every batch and stock level is traceable in real time.",
        bullets: [
          "Material receipts — batches created with QR codes and expiration dates",
          "Stock updates — movements between locations update levels automatically",
          "Stock-taking — manual adjustments reconcile physical and system counts",
          "Track & trace — full batch-level history across every location",
        ],
      },
      {
        title: "What's in the box",
        body: "Batch-level accuracy across every location, with transfers and stock-taking built in.",
        bullets: [
          "Material and product receipts with automatic GRN creation, linked to POs",
          "Automatic batch creation with QR codes and shelf-life control",
          "Real-time stock monitoring across multiple locations",
          "Supply vs demand comparison for shortage forecasting",
          "Automatic PR/PO generation and inventory transfers with batch traceability",
          "Stock management strategies: FIFO, LIFO, FEFO, ABC/XYZ classification",
          "Reservations for specific orders or buffer stock for warehouse planning",
        ],
      },
      {
        title: "Barcode work on the shop floor",
        body: "Warehouse and shop-floor staff work from a handheld terminal instead of a clipboard — every scan lands in the system immediately.",
        bullets: [
          "Stock review by location, batch or drum number",
          "QR-based stock-taking with instant variance against system quantities",
          "Transfers between locations, generated automatically from job order demand",
          "Batch split and merge, with the new batches inheriting full genealogy",
          "Label reprinting by batch or drum number, directly from the terminal",
        ],
      },
      {
        title: "Batch genealogy, end to end",
        body: "Trace any finished drum back to the raw material, or any suspect raw batch forward to every customer who received it.",
        bullets: [
          "Full raw-material-to-customer trace on every batch",
          "Forward trace for recalls: which orders, drums and customers are affected",
          "Backward trace for claims: which supplier lot and which shift produced it",
          "Quarantine of batches pending quality release",
          "Audit-ready history without assembling evidence by hand",
        ],
      },
    ],
    benefits: [
      { title: "Less frozen stock", text: "Real-time visibility keeps working capital out of the warehouse." },
      { title: "Fewer stock-taking mismatches", text: "Batch-level tracking keeps physical and system counts aligned." },
      { title: "Full traceability", text: "Every batch is tracked from receipt to shipment with QR codes." },
    ],
    faq: [
      { q: "Can I track expiration dates by batch?", a: "Yes, every batch carries a QR code and shelf-life data, supporting FEFO picking." },
      { q: "Does stocktaking update the system automatically?", a: "Discrepancies from a stocktake are recorded as manual adjustments and reconciled instantly." },
      { q: "Can I manage multiple warehouses?", a: "Yes, stock is tracked in real time across any number of locations with full transfer history." },
      { q: "Can warehouse staff work without a desktop?", a: "Yes — receipts, transfers, stock-taking, batch split/merge and label printing all run from a handheld barcode terminal." },
      { q: "Can a batch be blocked until quality approves it?", a: "Yes. Inspection status keeps the batch out of normal consumption until quality releases it." },
      { q: "Can I trace a defective batch to affected customers?", a: "Yes. Batch genealogy supports forward and backward traceability across receipts, production, splits, merges and shipments." },
    ],
    seo: {
      title: "Batch Traceability & Inventory Software | factory.online",
      description: "Control warehouse stock, quality quarantine, barcode labels and batch genealogy from raw material to affected customers.",
    },
  },

  planning: {
    slug: "planning",
    eyebrow: "Planning",
    title: "Find bottlenecks before they stop production",
    lead: "Rough-cut capacity and material planning, scenario modeling, and throughput forecasting.",
    heroBullets: [
      "RCCP gives managers a complete view of production load by days, weeks and months",
      "Instantly see critical materials, resource shortages or overloads",
      "Multi-scenario simulations compare options and test changes before you commit",
    ],
    sections: [
      {
        title: "See ahead — act with confidence",
        body: "The system shows capacity loads and material needs before problems arise, helping you choose the optimal scenario.",
        bullets: [
          "Rough-cut planning — resource and material requirements calculated by days, weeks or months",
          "Dependent demand analysis across linked orders and materials",
          "Scenario modeling to compare options before committing",
          "Simulation forecasting for throughput and risk",
          "Calendar integration for shifts, holidays and maintenance",
        ],
      },
      {
        title: "What's in the box",
        body: "Rough-cut capacity planning that answers whether the plan is even possible.",
        bullets: [
          "Rough-Cut Capacity Planning (RCCP)",
          "Workforce requirements planning with constrained/unconstrained availability models",
          "Material planning (MRP) with supply and safety stock consideration",
          "Automatic detection of bottlenecks and critical materials",
          "Scenario modeling with parallel calculations for multiple scenarios",
          "Calendars for shifts, holidays and maintenance schedules",
          "Visualization of resource load by days, weeks and months",
        ],
      },
      {
        title: "Scenarios, capacity modes and labour",
        body: "Plan the same demand several ways, then compare the consequences before anyone touches a machine.",
        bullets: [
          "ASAP, JIT and custom scenarios calculated in parallel",
          "Finite or infinite capacity, chosen per work centre",
          "Alternative work centres considered when the primary one is loaded",
          "MRP with supplier lead times, planned supplies and safety stock",
          "Labour requirements by profession and grade, plus subcontracting demand",
        ],
      },
    ],
    benefits: [
      { title: "Improved capacity", text: "Early detection of bottlenecks keeps schedules realistic." },
      { title: "Fewer material shortages", text: "MRP and safety stock management catch shortfalls before they bite." },
      { title: "More accurate forecasts", text: "Scenario modeling lets you test decisions before committing resources." },
    ],
    faq: [
      { q: "What is RCCP?", a: "Rough-Cut Capacity Planning gives a complete view of production load by days, weeks and months so you can see whether a plan is achievable." },
      { q: "Can I compare multiple planning scenarios?", a: "Yes, the system supports parallel simulations so you can compare options before committing." },
      { q: "Does planning account for calendars and holidays?", a: "Yes, shift patterns, holidays and maintenance schedules feed directly into the capacity plan." },
      { q: "What is the difference between finite and infinite capacity planning?", a: "Infinite capacity shows the demand a plan creates; finite capacity limits it to what the work centre can actually absorb. You choose per work centre." },
      { q: "Can I compare production schedule versions before release?", a: "Yes. Keep alternative versions, compare their impact and release the schedule the team has agreed to execute." },
      { q: "Can MRP respect fixed batches and production multiples?", a: "Yes. Demand can be split and grouped around batch, container and multiplicity rules that match the real process." },
    ],
    seo: {
      title: "MRP & Production Capacity Planning Software | factory.online",
      description: "Compare schedule versions, model batch rules and use MRP and capacity planning to expose shortages before production stops.",
    },
  },

  scheduling: {
    slug: "scheduling",
    eyebrow: "Scheduling",
    title: "A plan that lies is worse than no plan at all",
    lead: "We give you a schedule that always reflects reality — a production GPS, not a static picture.",
    heroBullets: [
      "Minute-by-minute execution schedules for manufacturing tasks",
      "Automatic rescheduling when breakdowns or urgent orders hit",
      "Always up-to-date plan with realistic statuses",
    ],
    sections: [
      {
        title: "A schedule that lives with production",
        body: "From job order creation to adaptive, real-time rescheduling.",
        bullets: [
          "Job orders creation from MTS/MTO manufacturing orders",
          "Assigning to work centres, considering availability and alternatives",
          "Sequence optimization with setups, changeovers and transfer times",
          "\"Production GPS\" — adaptive real-time rescheduling",
          "Maintenance sync with work centre maintenance schedules",
        ],
      },
      {
        title: "What's in the box",
        body: "Advanced planning and scheduling down to the work centre and the shift.",
        bullets: [
          "Automatic generation of job orders from MTS/MTO manufacturing orders",
          "Detailed minute-by-minute execution schedules",
          "Job order assignment across work centres with alternatives in mind",
          "Changeover time matrices for products and characteristics",
          "Dynamic rescheduling in case of downtime or repairs",
          "Prioritization of critical and urgent orders",
          "Transparent visualization of sequence and job order statuses",
        ],
      },
      {
        title: "The constraints a real schedule has to respect",
        body: "A schedule is only executable if it knows what actually limits the plant.",
        bullets: [
          "Lines, dies, compounds and crews treated as finite resources",
          "Setup and changeover matrices by product and characteristic",
          "Inter-operation transfer times between work centres",
          "Maintenance windows synchronised with the production schedule",
          "Instant rescheduling on breakdown, rush order or late material",
          "One executable schedule the whole shift works from",
        ],
      },
    ],
    benefits: [
      { title: "Higher productivity", text: "Reduced downtime keeps work centres running." },
      { title: "Less time lost on changeovers", text: "Sequencing accounts for setups and transfer times automatically." },
      { title: "Real-time transparency", text: "Everyone sees the same, always-current job order statuses." },
    ],
    faq: [
      { q: "What does 'Production GPS' mean?", a: "The schedule adapts automatically in real time — like GPS rerouting — when breakdowns, delays or urgent orders change the situation on the floor." },
      { q: "Can scheduling handle alternative work centres?", a: "Yes, job orders are assigned considering availability and alternative work centres to optimize equipment load." },
      { q: "Does it consider changeover times?", a: "Yes, changeover time matrices per product and characteristic are factored into sequencing." },
      { q: "How fast can the schedule react to a breakdown?", a: "Rescheduling is immediate: affected job orders are resequenced across available work centres and the shop floor sees the new plan straight away." },
      { q: "Can planners move many jobs to another work centre at once?", a: "Yes. Work centres and other execution targets can be updated in bulk when equipment availability changes." },
      { q: "Can compatible job orders be grouped into one run?", a: "Yes. Jobs can be combined for an efficient run and separated again while retaining their source-order links." },
      { q: "What problems does production scheduling software solve?", a: "It replaces static spreadsheets with a live, executable schedule: realistic start and finish times, finite work-centre capacity, changeover matrices, and instant replanning when breakdowns, rush orders or late materials change the day." },
      { q: "Is factory.online suitable for small and mid-sized manufacturers?", a: "Yes. Scheduling works out of the box with your existing orders, BOMs and work centres — no months-long implementation or dedicated planning department required." },
    ],
    seo: {
      title: "Production Scheduling Software for Manufacturers | factory.online",
      description: "Manufacturing scheduling software with Gantt timelines, work-centre constraints, job grouping and automatic rescheduling when the shop floor changes.",
    },
  },

  cost: {
    slug: "cost",
    eyebrow: "Product Costing",
    title: "Do you know your costs — or just guess them?",
    lead: "Transparent plan/actual figures. See where profit is leaking and make decisions based on data, not intuition.",
    heroBullets: [
      "Planned cost based on standard prices and rates",
      "Actual cost based on real expenses and time",
      "Plan vs actual comparison for precise management decisions",
    ],
    sections: [
      {
        title: "Costs without guesswork",
        body: "Know exactly how much production costs — with plan/actual in real time.",
        bullets: [
          "Standard costing — costs calculated from standard rates",
          "Actual expenses collection from real stock prices and labour",
          "Actual costing based on real operation times",
          "Standard vs actual comparison with variance analysis",
        ],
      },
      {
        title: "What's in the box",
        body: "Know the real cost of every order — standard, actual, and the gap between them.",
        bullets: [
          "Planned cost calculation by direct costs (materials, work centres, manpower)",
          "Use of standard material prices and labour rates",
          "Actual cost calculation based on real stock prices and actual operation times",
          "Cost accounting for finished goods and each input item",
          "Plan vs actual comparison with variance analysis",
          "Automated reports for management decisions",
        ],
      },
      {
        title: "Standard, actual and the gap between them",
        body: "Costing is calculated from the same BOMs, routings and shop-floor records the plant runs on — not re-typed into a spreadsheet.",
        bullets: [
          "Standard cost built from BOMs, routings and resource rates",
          "What-if simulation before quoting a price or accepting an order",
          "Actual cost from recorded consumption, machine time and labour",
          "Variance analysis by product, batch, material and resource",
          "Margin visible per order line, not only at month end",
        ],
      },
    ],
    benefits: [
      { title: "Smaller cost deviations", text: "Standard and actual costs are compared automatically, order by order." },
      { title: "Faster decisions", text: "Transparent cost data replaces intuition with evidence." },
      { title: "Fewer indirect losses", text: "Accurate calculations reveal where inaccurate estimates were costing you." },
    ],
    faq: [
      { q: "How is standard cost calculated?", a: "From standard material prices, labour rates and work centre costs assigned to the BOM and routing." },
      { q: "How is actual cost tracked?", a: "From real stock prices and the actual operation times recorded on the shop floor." },
      { q: "Can I see the variance between plan and actual?", a: "Yes, standard vs actual comparisons highlight deviations by category and item for management review." },
      { q: "Where does actual cost come from?", a: "From the shop floor — recorded material consumption, machine time and labour on each job order, compared against the standard." },
      { q: "Can purchased services be included in product cost?", a: "Yes. Received services and subcontracted work can flow into the same cost picture as materials and operations." },
      { q: "Can I review inventory value by the real business date?", a: "Yes. Historical stock movement follows the business date, supporting accurate period analysis and reconciliation." },
    ],
    seo: {
      title: "Product Costing Software | factory.online",
      description: "Compare standard and actual manufacturing cost, including materials, labour, subcontracted services and historical stock movements.",
    },
  },

  "technical-data": {
    slug: "technical-data",
    eyebrow: "Master and Product Data",
    title: "The foundation your production relies on",
    lead: "Products, materials, specifications, routings and work centres — all in one system. No scattered files or chaotic reference sheets.",
    heroBullets: [
      "A single database for products, materials and configurations",
      "Matrix BOMs replace hundreds of manual variants",
      "Managed process for changes to BOMs and routings at the order level",
    ],
    sections: [
      {
        title: "Data that holds production together",
        body: "A single catalog of BOMs, routings and work centres ensures transparency and controlled changes at any moment.",
        bullets: [
          "Fill in master data for items, classes and characteristics",
          "Enter BOMs and build multi-level product structures",
          "Define routings with alternative sequences",
          "Standardize resources and work centres",
          "Use consistent data in production without rework",
          "Manage changes with controlled version transitions",
        ],
      },
      {
        title: "What's in the box",
        body: "One clean backbone of items, BOMs and routings that every module reads from.",
        bullets: [
          "Product catalog with configurations, markings and packaging variants",
          "Material catalog, including waste, services and packaging",
          "Matrix BOMs for configurable product families",
          "Automatic calculation of multi-level BOMs, with material substitutes tracked",
          "Catalog of operations with time standards and alternative routings",
          "Locations, work centres and standardized transfer times",
          "Notifications and controlled transition of orders to new BOM/routing versions",
        ],
      },
      {
        title: "The data every other module reads",
        body: "Master data, BOMs, routings, resources, machines and packaging are the foundation planning and costing stand on.",
        bullets: [
          "Item, class and characteristic master data maintained in one place",
          "BOMs and routings versioned, with controlled transition for open orders",
          "Machines, work centres and their performance data",
          "Packaging data — drums, coils and cut lengths — as first-class objects",
          "Every planning, scheduling and costing result traceable to the data behind it",
        ],
      },
    ],
    benefits: [
      { title: "Lower labour cost for BOMs", text: "Matrix BOMs replace hundreds of manual variants with one structure." },
      { title: "Fewer errors", text: "One source of truth for BOMs and routings cuts mistakes across the plant." },
      { title: "Faster process updates", text: "Changes propagate through a controlled, visible version process." },
    ],
    faq: [
      { q: "What is a Matrix BOM?", a: "One BOM definition that covers an entire configurable product family, replacing hundreds of manually maintained variants." },
      { q: "Can I use alternative routings and work centres?", a: "Yes, alternative routings and work centres with performance data are supported to optimise equipment use." },
      { q: "How are BOM and routing changes managed?", a: "Changes trigger notifications, and open orders can continue on the old version or move to the new one under a controlled process." },
      { q: "Is packaging part of master data?", a: "Yes. Drums, coils and cut lengths are modelled as data, so packaging economics feed planning and costing instead of being estimated afterwards." },
      { q: "Can we add our own fields without a development project?", a: "Yes. Custom fields adapt document headers and lines to industry, customer and internal control requirements." },
      { q: "Can master data be updated from Excel safely?", a: "Yes. Bulk updates include a preview step so teams can check changes before importing them." },
    ],
    seo: {
      title: "Master & Product Data Management | factory.online",
      description: "Manage BOMs, routings, work centres, custom fields and controlled Excel updates in one manufacturing data backbone.",
    },
  },
};

/** Look up a feature page by slug; throws for unknown slugs at build time. */
export function featurePage(slug: string): FeaturePageContent {
  const page = FEATURE_PAGES[slug];
  if (!page) throw new Error(`Unknown feature page: ${slug}`);
  return page;
}
